import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { cn } from '@/lib/cn'

export type ToastTone = 'success' | 'error' | 'info' | 'warning'

export interface ToastOptions {
  description?: string
  /** Milissegundos até sumir. `null` mantém na tela até o usuário fechar. */
  duration?: number | null
  actionLabel?: string
  onAction?: () => void
}

export interface ToastRecord extends ToastOptions {
  id: string
  tone: ToastTone
  title: string
}

type ToastCreator = (title: string, options?: ToastOptions) => string

export interface ToastApi {
  success: ToastCreator
  error: ToastCreator
  info: ToastCreator
  warning: ToastCreator
}

export interface ToastContextValue {
  toast: ToastApi
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const DEFAULT_DURATION = 5000

const TONE_STYLE: Record<ToastTone, { icon: typeof Info; className: string }> = {
  success: { icon: CheckCircle2, className: 'text-[var(--success)]' },
  error: { icon: XCircle, className: 'text-[var(--danger)]' },
  warning: { icon: AlertTriangle, className: 'text-[var(--warning)]' },
  info: { icon: Info, className: 'text-[var(--info)]' },
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast precisa estar dentro de <ToastProvider>.')
  return context
}

export interface ToastProviderProps {
  children: ReactNode
  /** Quantidade máxima de avisos simultâneos; os mais antigos saem primeiro. */
  max?: number
}

export function ToastProvider({ children, max = 4 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastRecord[]>([])
  const counter = useRef(0)

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((item) => item.id !== id))
  }, [])

  const push = useCallback(
    (tone: ToastTone, title: string, options?: ToastOptions): string => {
      counter.current += 1
      const id = `toast-${counter.current}`
      const record: ToastRecord = {
        ...options,
        id,
        tone,
        title,
        // Erros exigem leitura e ação: nunca somem sozinhos.
        duration:
          options?.duration !== undefined
            ? options.duration
            : tone === 'error'
              ? null
              : DEFAULT_DURATION,
      }
      setToasts((current) => [...current, record].slice(-max))
      return id
    },
    [max],
  )

  const value = useMemo<ToastContextValue>(
    () => ({
      dismiss,
      toast: {
        success: (title, options) => push('success', title, options),
        error: (title, options) => push('error', title, options),
        info: (title, options) => push('info', title, options),
        warning: (title, options) => push('warning', title, options),
      },
    }),
    [push, dismiss],
  )

  const assertive = toasts.filter((item) => item.tone === 'error')
  const polite = toasts.filter((item) => item.tone !== 'error')

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="region"
        aria-label="Notificações"
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
      >
        <ol aria-live="assertive" className="flex flex-col gap-2">
          {assertive.map((item) => (
            <ToastItem key={item.id} toast={item} onDismiss={dismiss} />
          ))}
        </ol>
        <ol aria-live="polite" className="flex flex-col gap-2">
          {polite.map((item) => (
            <ToastItem key={item.id} toast={item} onDismiss={dismiss} />
          ))}
        </ol>
      </div>
    </ToastContext.Provider>
  )
}

interface ToastItemProps {
  toast: ToastRecord
  onDismiss: (id: string) => void
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [paused, setPaused] = useState(false)
  const { icon: Icon, className: toneClass } = TONE_STYLE[toast.tone]
  const { id, duration } = toast

  // O contador reinicia ao sair do hover/foco — simples e previsível para o usuário.
  useEffect(() => {
    if (paused || !duration) return
    const timer = window.setTimeout(() => onDismiss(id), duration)
    return () => window.clearTimeout(timer)
  }, [paused, duration, id, onDismiss])

  return (
    <li
      className={cn(
        'sh-card pointer-events-auto flex animate-[sh-slide-in-right_180ms_ease-out] gap-3 p-3.5',
      )}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <Icon aria-hidden="true" className={cn('mt-0.5 size-5 shrink-0', toneClass)} />

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-5 text-[var(--text-primary)]">{toast.title}</p>
        {toast.description && (
          <p className="mt-0.5 text-sm leading-5 text-[var(--text-secondary)]">
            {toast.description}
          </p>
        )}
        {toast.actionLabel && (
          <button
            type="button"
            onClick={() => {
              toast.onAction?.()
              onDismiss(toast.id)
            }}
            className="mt-2 rounded-md text-sm font-semibold text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
          >
            {toast.actionLabel}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label={`Fechar aviso: ${toast.title}`}
        className="-m-1 inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-inset)] hover:text-[var(--text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
      >
        <X aria-hidden="true" className="size-4" />
      </button>
    </li>
  )
}
